import { Component, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  DateRange,
  extractDateRange,
  extractDefaultStoreIds,
  extractNumberArr,
  HxMoneyFormatPipe,
  HxReportService,
  HxUiLabelPipe,
  isoDate,
  ProductStatus,
  RevenueByProductModel,
  SalesByCategoryModel,
  SalesByProductModel,
  StoreBasicModel,
  toRouterQueryParams
} from 'hx-services';
import { saveAs } from 'file-saver';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HxDateRangeSelectComponent, HxLoaderComponent, HxProductCategorySelectComponent, HxStoreSelectComponent } from 'hx-component';
import { NgClass, NgForOf, NgIf } from '@angular/common';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { NzThMeasureDirective } from 'ng-zorro-antd/table';
import { HcToastrService } from '@manager-app/service/toastr.service';
import { NgbNav, NgbNavContent, NgbNavItem, NgbNavLink, NgbNavLinkBase, NgbNavOutlet } from '@ng-bootstrap/ng-bootstrap';

interface StoreHeader {
  id: number;
  name?: string;
}

@Component({
  selector: 'app-revenue-by-product',
  templateUrl: './revenue-by-product.component.html',
  imports: [
    FormsModule,
    HxDateRangeSelectComponent,
    HxLoaderComponent,
    HxProductCategorySelectComponent,
    HxStoreSelectComponent,
    HxUiLabelPipe,
    NgForOf,
    NgIf,
    TranslocoPipe,
    NzThMeasureDirective,
    NgClass,
    NgbNav,
    NgbNavContent,
    NgbNavLink,
    NgbNavLinkBase,
    NgbNavItem,
    NgbNavOutlet,
    HxMoneyFormatPipe,
  ],
  styleUrls: ['./revenue-by-product.component.css']
})
export class RevenueByProductComponent implements OnInit {
  isLoading = {
    list: false,
    generate: false,
    category: false,
    discard: false,
    product: false,
  };
  params: {
    storeIds?: number[],
    date?: DateRange,
    categoryIds?: number[];
  } = {};
  brandId?: number;
  list: RevenueByProductModel[] = [];
  listCategory: SalesByCategoryModel[] = [];
  listCategorySortedByAmount: SalesByCategoryModel[] = [];
  listDiscarded: SalesByCategoryModel[] = [];

  listServicesSortCount: SalesByProductModel[] = [];
  listServicesSortValue: SalesByProductModel[] = [];
  totalServicesCount = 0;
  totalServicesValue = 0;

  listStandardAndMaxCakesCount: SalesByProductModel[] = [];
  listStandardAndMaxCakesValue: SalesByProductModel[] = [];
  totalStandardAndMaxCakesCount = 0;
  totalStandardAndMaxCakesValue = 0;

  listNotStandardCakesCount: SalesByProductModel[] = [];
  listNotStandardCakesValue: SalesByProductModel[] = [];
  totalNotStandardCakesCount = 0;
  totalNotStandardCakesValue = 0;

  listOtherCakesCount: SalesByProductModel[] = [];
  listOtherCakesValue: SalesByProductModel[] = [];
  totalOtherCakesCount = 0;
  totalOtherCakesValue = 0;

  listAdditionalNomenclatureCount: SalesByProductModel[] = [];
  listAdditionalNomenclatureValue: SalesByProductModel[] = [];
  totalAdditionalNomenclatureCount = 0;
  totalAdditionalNomenclatureValue = 0;

  listOthersCount: SalesByProductModel[] = [];
  listOthersValue: SalesByProductModel[] = [];
  totalOthersCount = 0;
  totalOthersValue = 0;

  activeNav?: string;
  discardTotalAmount?: number;
  discardTotalValue?: number;

  private readonly reportService = inject(HxReportService);
  private readonly aRoute = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly toastr = inject(HcToastrService);
  private readonly tr = inject(TranslocoService);

  constructor() {
    this.activeNav = 'report';
    this.aRoute.queryParamMap.pipe(takeUntilDestroyed()).subscribe(async paramMap => {
      const asArrFn = (name: string, mapFn: (key: string) => any) => paramMap.get(name) ? paramMap.get(name).split(',').map(mapFn) : undefined;
      this.params.date = extractDateRange('date', paramMap) ?? {from: isoDate(new Date()), to: isoDate(new Date())};
      this.params.categoryIds = extractNumberArr('categoryIds', paramMap, []);
      this.params.storeIds = asArrFn('storeIds', id => Number(id)) ?? extractDefaultStoreIds();
      if (this.params.storeIds && this.params.categoryIds) {
        this.onTabChanged();
      }
    });
  }

  ngOnInit() {
    this.brandId = localStorage.getItem('defaultBrandId') ? Number(localStorage.getItem('defaultBrandId')) : undefined;
  }

  resetFilter() {
    this.params.date = {from: isoDate(new Date()), to: isoDate(new Date())};
    this.navigate();
  }

  navigate() {
    this.router.navigate(['./'], {
      queryParams: toRouterQueryParams(this.params),
      relativeTo: this.aRoute
    });
  }

  onStoreChanged(store?: StoreBasicModel | StoreBasicModel[]) {
    if (Array.isArray(store)) {
      return;
    }
    this.brandId = store?.brandId;
    this.navigate();
  }

  onTabChanged() {
    if (this.activeNav === 'report') {
      this.loadByProduct();
    } else if (this.activeNav === 'report1') {
      this.loadSalesByCategoryWithRecieveStatus();
      this.loadSalesByCategoryWithDiscardStatus();
      this.loadSalesByProduct();
    }
  }

  async generate() {
    if (this.activeNav === 'report') {
      this.generateReport0();
    } else if (this.activeNav === 'report1') {
      this.generateReport1();
    }
  }

  private async generateReport1() {
    this.isLoading.generate = true;
    try {
      const response = await this.reportService.generateSalesByCategoryAndProduct({
        fromDate: this.params.date.from,
        toDate: this.params.date.to,
        storeIds: this.params.storeIds,
        categoryIds: this.params.categoryIds
      });
      const contentDispositionHeader = response.headers.get('Content-Disposition');
      const result = contentDispositionHeader.split(';')[1].trim().split('=')[1];
      const blob = new Blob([response.body], {type: 'application/octet-stream'});
      saveAs(blob, result);
      this.toastr.success(this.tr.translate('received-report.ts.success'));
    } catch (e: any) {
      console.log(e);
    } finally {
      this.isLoading.generate = false;
    }
  }

  private async generateReport0() {
    this.isLoading.generate = true;
    try {
      const response = await this.reportService.generateRevenueByProducts({
        fromDate: this.params.date.from,
        toDate: this.params.date.to,
        storeIds: this.params.storeIds,
        categoryIds: this.params.categoryIds
      });
      const contentDispositionHeader = response.headers.get('Content-Disposition');
      const result = contentDispositionHeader.split(';')[1].trim().split('=')[1];
      const blob = new Blob([response.body], {type: 'application/octet-stream'});
      saveAs(blob, result);
      this.toastr.success(this.tr.translate('received-report.ts.success'));
    } catch (e: any) {
      console.log(e);
    } finally {
      this.isLoading.generate = false;
    }
  }

  private async loadByProduct() {
    this.isLoading.list = true;
    this.list = [];
    try {
      this.list = await this.reportService.getRevenueByProducts({
        fromDate: this.params.date.from,
        toDate: this.params.date.to,
        storeIds: this.params.storeIds,
        categoryIds: this.params.categoryIds
      });
    } finally {
      this.isLoading.list = false;
    }
  }

  private async loadSalesByCategoryWithRecieveStatus() {
    this.isLoading.category = true;
    this.list = [];
    try {
      this.listCategory = await this.reportService.getSalesByCategory({
        fromDate: this.params.date.from,
        toDate: this.params.date.to,
        storeIds: this.params.storeIds,
        categoryIds: this.params.categoryIds,
        productStatuses: [ProductStatus.RECEIVED]
      });

      this.listCategorySortedByAmount = [...this.listCategory].sort((a, b) => b.percentOfTotal - a.percentOfTotal);
      this.listCategory.sort((a, b) => b.percentOfTotal - a.percentOfTotal);
    } finally {
      this.isLoading.category = false;
    }
  }

  private async loadSalesByCategoryWithDiscardStatus() {
    this.isLoading.discard = true;
    this.list = [];
    try {
      this.listDiscarded = await this.reportService.getSalesByCategory({
        fromDate: this.params.date.from,
        toDate: this.params.date.to,
        storeIds: this.params.storeIds,
        categoryIds: this.params.categoryIds,
        productStatuses: [ProductStatus.DISCARDED]
      });

      this.discardTotalAmount = this.listDiscarded[0]?.totalAmountAll ?? 0;
      this.discardTotalValue = this.listDiscarded[0]?.totalAll ?? 0;

    } finally {
      this.isLoading.discard = false;
    }
  }

  private async loadSalesByProduct() {
    this.isLoading.product = true;
    this.list = [];
    try {
      const listProduct = await this.reportService.getSalesByProduct({
        fromDate: this.params.date.from,
        toDate: this.params.date.to,
        storeIds: this.params.storeIds,
        categoryIds: this.params.categoryIds
      });
      //1. Все торты (standart, max, mini, bento, пироги и рулеты 1, 8, 103, 106, 107, 108, 109, 83, 84, 85), 2. Доставка и услуги (3, 2),
      // 3. Весовые и ярус (7, 11, 12, 13, 14, 46, 79, 86, 98, 104), 4. Пирожное (6, 110), 5. Доп. номенклатура (4). И остальные

      const services = listProduct.filter(r => [3,2].includes(r.categoryId));
      this.listServicesSortCount = services.sort((a, b) => (b.totalCount ?? 0) - (a.totalCount ?? 0));
      this.listServicesSortValue = services.sort((a, b) => (b.totalValue ?? 0) - (a.totalValue?? 0));
      this.totalServicesCount = services.reduce((acc, cur) => acc + cur.totalCount, 0);
      this.totalServicesValue = services.reduce((acc, cur) => acc + cur.totalValue, 0);

      const standardAndMaxCakes = listProduct.filter(r => [1,8,103,106,107,108,109,83,84,85].includes(r.categoryId));
      this.listStandardAndMaxCakesCount = [...standardAndMaxCakes].sort((a, b) => (b.totalCount ?? 0) - (a.totalCount ?? 0));
      this.listStandardAndMaxCakesValue = [...standardAndMaxCakes].sort((a, b) => (b.totalValue ?? 0) - (a.totalValue ?? 0));
      this.totalStandardAndMaxCakesCount = [...standardAndMaxCakes].reduce((acc, cur) => acc + cur.totalCount, 0);
      this.totalStandardAndMaxCakesValue = [...standardAndMaxCakes].reduce((acc, cur) => acc + cur.totalValue, 0);

      const notStandardCakes = listProduct.filter(r => [7,11,12,13,14,46,79,86,98,104].includes(r.categoryId));
      this.listNotStandardCakesCount = [...notStandardCakes].sort((a, b) => (b.totalCount ?? 0) - (a.totalCount ?? 0));
      this.listNotStandardCakesValue = [...notStandardCakes].sort((a, b) => (b.totalValue ?? 0) - (a.totalValue ?? 0));
      this.totalNotStandardCakesCount = [...notStandardCakes].reduce((acc, cur) => acc + cur.totalCount, 0);
      this.totalNotStandardCakesValue = [...notStandardCakes].reduce((acc, cur) => acc + cur.totalValue, 0);

      const otherCakes = listProduct.filter(r => [6,110].includes(r.categoryId));
      this.listOtherCakesCount = [...otherCakes].sort((a, b) => (b.totalCount ?? 0) - (a.totalCount ?? 0));
      this.listOtherCakesValue = [...otherCakes].sort((a, b) => (b.totalValue ?? 0) - (a.totalValue ?? 0));
      this.totalOtherCakesCount = [...otherCakes].reduce((acc, cur) => acc + cur.totalCount, 0);
      this.totalOtherCakesValue = [...otherCakes].reduce((acc, cur) => acc + cur.totalValue, 0);

      const additionalNomenclature = listProduct.filter(r => [4].includes(r.categoryId));
      this.listAdditionalNomenclatureCount = [...additionalNomenclature].sort((a, b) => (b.totalCount ?? 0) - (a.totalCount ?? 0));
      this.listAdditionalNomenclatureValue = [...additionalNomenclature].sort((a, b) => (b.totalValue ?? 0) - (a.totalValue ?? 0));
      this.totalAdditionalNomenclatureCount = [...additionalNomenclature].reduce((acc, cur) => acc + cur.totalCount, 0);
      this.totalAdditionalNomenclatureValue = [...additionalNomenclature].reduce((acc, cur) => acc + cur.totalValue, 0);

      const others = listProduct.filter(r => ![4,6,110,7,11,12,13,14,46,79,86,98,104,1,8,103,106,107,108,109,83,84,85,3,2].includes(r.categoryId));
      this.listOthersCount = [...others].sort((a, b) => (b.totalCount ?? 0) - (a.totalCount ?? 0));
      this.listOthersValue = [...others].sort((a, b) => (b.totalValue ?? 0) - (a.totalValue ?? 0));
      this.totalOthersCount = [...others].reduce((acc, cur) => acc + cur.totalCount, 0);
      this.totalOthersValue = [...others].reduce((acc, cur) => acc + cur.totalValue, 0);
    } finally {
      this.isLoading.product = false;
    }
  }

}
